import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Pencil, Trash2, KeyRound } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [services, setServices] = useState([]);
  const [svcOpen, setSvcOpen] = useState(false);
  const [svcEditing, setSvcEditing] = useState(null);
  const [svcForm, setSvcForm] = useState({ name: "", description: "", active: true });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data || {}));
    api.get("/services-catalog").then((r) => setServices(r.data));
  }, []);

  const isSuper = user?.role === "super_admin";
  const canEdit = isSuper;

  const saveSettings = async () => {
    try { await api.put("/settings", s); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openSvc = (svc = null) => {
    setSvcEditing(svc);
    setSvcForm(svc ? { name: svc.name, description: svc.description || "", active: !!svc.active } : { name: "", description: "", active: true });
    setSvcOpen(true);
  };
  const submitSvc = async () => {
    if (!svcForm.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (svcEditing) await api.put(`/services-catalog/${svcEditing.id}`, svcForm);
      else await api.post("/services-catalog", svcForm);
      toast.success(svcEditing ? "Service updated" : "Service added");
      setSvcOpen(false); setSvcEditing(null);
      const r = await api.get("/services-catalog"); setServices(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteSvc = async (svc) => {
    if (!window.confirm(`Delete "${svc.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/services-catalog/${svc.id}`);
      toast.success("Service deleted");
      const r = await api.get("/services-catalog"); setServices(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitPw = async () => {
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New password and confirmation don't match"); return; }
    setPwLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password changed successfully");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setPwLoading(false);
  };

  if (!s) return null;

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Configuration</div>
        <h1 className="text-4xl font-black tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Company info, service catalog, and your account.</p>
      </div>

      {/* Company */}
      <section className="border border-border rounded-md p-6 bg-card space-y-4">
        <h2 className="font-display font-extrabold text-lg">Company</h2>
        <div>
          <Label className="font-mono text-[10px] uppercase">Company Name</Label>
          <Input data-testid="settings-company" disabled={!canEdit} value={s.company_name || ""} onChange={(e) => setS({ ...s, company_name: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="font-mono text-[10px] uppercase">Currency</Label><Input disabled={!canEdit} value={s.currency || "INR"} onChange={(e) => setS({ ...s, currency: e.target.value })} /></div>
          <div><Label className="font-mono text-[10px] uppercase">Default Tax %</Label><Input type="number" disabled={!canEdit} value={s.tax_default || 18} onChange={(e) => setS({ ...s, tax_default: parseFloat(e.target.value || 0) })} /></div>
        </div>
        {canEdit ? <Button data-testid="settings-save-btn" onClick={saveSettings} className="bg-primary hover:bg-primary/90">Save changes</Button>
          : <p className="text-xs text-muted-foreground">Only super admins can modify these settings.</p>}
      </section>

      {/* Services catalog */}
      <section className="border border-border rounded-md p-6 bg-card space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display font-extrabold text-lg">Service catalog</h2>
            <p className="text-xs text-muted-foreground">{services.length} services. Manage what your agency offers.</p>
          </div>
          {isSuper && (
            <Button size="sm" data-testid="svc-add-btn" onClick={() => openSvc()} className="bg-primary hover:bg-primary/90"><Plus size={14} className="mr-1" />Add service</Button>
          )}
        </div>
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2">Status</th>
                {isSuper && <th className="px-3 py-2 w-24 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {services.length === 0 && <tr><td colSpan={isSuper ? 4 : 3} className="p-6 text-center text-muted-foreground">No services defined.</td></tr>}
              {services.map((svc) => (
                <tr key={svc.id} className="border-t border-border" data-testid={`svc-row-${svc.id}`}>
                  <td className="px-3 py-2 font-semibold">{svc.name}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{svc.description || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono uppercase tracking-widest">
                    {svc.active ? <span className="text-emerald-600 dark:text-emerald-400">Active</span> : <span className="text-muted-foreground">Inactive</span>}
                  </td>
                  {isSuper && (
                    <td className="px-3 py-2 text-right">
                      <button data-testid={`svc-edit-${svc.id}`} onClick={() => openSvc(svc)} className="p-1.5 hover:bg-muted rounded transition-colors" aria-label="Edit"><Pencil size={13} /></button>
                      <button data-testid={`svc-delete-${svc.id}`} onClick={() => deleteSvc(svc)} className="p-1.5 hover:bg-muted rounded transition-colors text-destructive" aria-label="Delete"><Trash2 size={13} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!isSuper && <p className="text-xs text-muted-foreground">Only super admins can add, edit or delete services.</p>}
      </section>

      {/* Change my password */}
      <section className="border border-border rounded-md p-6 bg-card space-y-4">
        <div>
          <h2 className="font-display font-extrabold text-lg flex items-center gap-2"><KeyRound size={18} />Change my password</h2>
          <p className="text-xs text-muted-foreground">Updating your password will keep you signed in on this device.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label className="font-mono text-[10px] uppercase">Current password</Label><Input data-testid="pw-current" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></div>
          <div />
          <div><Label className="font-mono text-[10px] uppercase">New password (min 8)</Label><Input data-testid="pw-new" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></div>
          <div><Label className="font-mono text-[10px] uppercase">Confirm new password</Label><Input data-testid="pw-confirm" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} /></div>
        </div>
        <Button data-testid="pw-submit" onClick={submitPw} disabled={pwLoading || !pw.current_password || !pw.new_password} className="bg-primary hover:bg-primary/90">
          {pwLoading ? "Updating…" : "Update password"}
        </Button>
      </section>

      {/* Current user */}
      <section className="border border-border rounded-md p-6 bg-card">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Signed in as</div>
        <div className="mt-2 text-sm space-y-1">
          <div><span className="font-mono text-muted-foreground">Name:</span> <span className="font-semibold">{user?.name}</span></div>
          <div><span className="font-mono text-muted-foreground">Email:</span> {user?.email}</div>
          <div><span className="font-mono text-muted-foreground">Role:</span> <span className="font-mono uppercase">{user?.role?.replace("_", " ")}</span></div>
          <div><span className="font-mono text-muted-foreground">Finance access:</span> {user?.finance_access ? "Yes" : "No"}</div>
        </div>
      </section>

      {/* Service dialog */}
      <Dialog open={svcOpen} onOpenChange={(v) => { setSvcOpen(v); if (!v) setSvcEditing(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{svcEditing ? "Edit service" : "Add service"}</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><Label className="font-mono text-[10px] uppercase">Name *</Label><Input data-testid="svc-form-name" value={svcForm.name} onChange={(e) => setSvcForm({ ...svcForm, name: e.target.value })} /></div>
            <div><Label className="font-mono text-[10px] uppercase">Description</Label><Input value={svcForm.description} onChange={(e) => setSvcForm({ ...svcForm, description: e.target.value })} placeholder="Short summary" /></div>
            <div className="flex items-center justify-between border border-border rounded-md p-3">
              <div>
                <div className="text-sm font-semibold">Active</div>
                <div className="text-xs text-muted-foreground">Inactive services stay in history but won't show for new work.</div>
              </div>
              <Switch data-testid="svc-form-active" checked={svcForm.active} onCheckedChange={(v) => setSvcForm({ ...svcForm, active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSvcOpen(false); setSvcEditing(null); }}>Cancel</Button>
            <Button data-testid="svc-form-submit" onClick={submitSvc} className="bg-primary hover:bg-primary/90">{svcEditing ? "Save changes" : "Add service"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
